import { ref, computed, type Ref } from "vue"
import { defineStore } from "pinia"
import { Instruction } from "@/models/instruction.model"
import { InstructionFeedbackStatuses } from "@/enums/models/instruction-feedback-statuses"
import moment, { type Moment } from "moment"
import { merge } from "lodash-es"

export const useAppInstructionStore = defineStore("appInstructionStore", () => {
  const instructionsByDate: Ref<{ [date: string]: Instruction[] }> = ref({})

  const instructions = computed(() => {
    return Object.values(instructionsByDate.value).flat()
  })

  const selectedDate: Ref<Moment> = ref(moment().startOf("day"))

  const instructionsForSelectedDate = computed(() => {
    if (hasInstructionsForDate(selectedDate.value)) {
      return instructionsByDate.value[selectedDate.value.format("YYYY-MM-DD")]
    }
    return []
  })

  function incrementInstructionStatus(instruction: Instruction) {
    if (instruction.feedback) {
      instruction.feedback.status =
        instruction.feedback.status === InstructionFeedbackStatuses.IN_PROGRESS
          ? InstructionFeedbackStatuses.COMPLETED
          : InstructionFeedbackStatuses.IN_PROGRESS
    }
  }

  const allInstructionsDone = computed(() => {
    return instructionsForSelectedDate.value.every(
      (instruction) => instruction.feedback?.status === InstructionFeedbackStatuses.COMPLETED
    )
  })

  function hasInstructionsForDate(date: Moment) {
    return instructionsByDate.value[date.format("YYYY-MM-DD")] !== undefined
  }

  function clearInstructions() {
    instructionsByDate.value = {}
  }

  function setInstructionsForDate(date: Moment, instructions: Instruction[]) {
    const dateString = date.format("YYYY-MM-DD")

    // Initialize the array for the date if it doesn't exist
    if (!instructionsByDate.value[dateString]) {
      instructionsByDate.value[dateString] = []
    }

    // Create a map of new instructions by their ID for quick access
    const newInstructionsMap = new Map(instructions.map((instr) => [instr.id, instr]))

    // Filter out instructions that are not in the new instructions list
    instructionsByDate.value[dateString] = instructionsByDate.value[dateString].filter((instr) =>
      newInstructionsMap.has(instr.id)
    )

    // Now add or replace instructions from the new list
    for (const instruction of instructions) {
      const existingIndex = instructionsByDate.value[dateString].findIndex(
        (instr) => instr.id === instruction.id
      )
      if (existingIndex !== -1) {
        // Replace the existing instruction, omitting feedback to prevent overwriting while user is editing.
        delete instruction.feedback
        merge(
          instructionsByDate.value[dateString][existingIndex],
          instructionsByDate.value[dateString][existingIndex],
          instruction
        )
      } else {
        // Add new instruction
        instructionsByDate.value[dateString].push(instruction)
      }
    }

    // Sort instructions by order
    instructionsByDate.value[dateString].sort((a, b) => {
      return a?.timeEnd?.diff(b?.timeEnd) ?? 0
    })
  }

  return {
    instructionsByDate,
    instructions,
    instructionsForSelectedDate,
    selectedDate,
    allInstructionsDone,
    incrementInstructionStatus,
    hasInstructionsForDate,
    clearInstructions,
    setInstructionsForDate
  }
})
